import React, { useState, useMemo, useRef } from 'react';
import { guardarConfigRegistroCalidad } from '../../servicios/calidadService';
import '../estilos/Calidad.css';

function CheckboxIndeterminate({ checked, indeterminate, onChange, onClick }) {
  const ref = useRef(null);
  useMemo(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={onClick}
      style={{ width: 15, height: 15, accentColor: '#16a34a', flexShrink: 0 }}
    />
  );
}

function ChipProtocolo({ texto, onQuitar }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
      borderRadius: 999, padding: '3px 8px', fontSize: 12, fontWeight: 600
    }}>
      {texto}
      <button
        type="button"
        onClick={onQuitar}
        style={{
          border: 'none', background: 'transparent', color: '#1d4ed8',
          cursor: 'pointer', fontWeight: 700, fontSize: 13, lineHeight: 1, padding: 0
        }}
        title="Quitar protocolo"
      >
        ×
      </button>
    </span>
  );
}

function PanelProtocolos({ protocolos, onCambiar, onQuitarGrupo }) {
  const [texto, setTexto] = useState('');

  const agregar = () => {
    const valor = texto.trim();
    if (!valor) return;
    if (!protocolos.includes(valor)) {
      onCambiar([...protocolos, valor]);
    }
    setTexto('');
  };

  const quitar = (i) => {
    onCambiar(protocolos.filter((_, idx) => idx !== i));
  };

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        marginTop: 6, marginBottom: 8, padding: '10px 12px',
        background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {protocolos.length === 0 ? (
          <span style={{ fontSize: 12, color: '#dc2626' }}>
            Agrega al menos un protocolo a controlar para esta partida.
          </span>
        ) : (
          protocolos.map((p, i) => (
            <ChipProtocolo key={i} texto={p} onQuitar={() => quitar(i)} />
          ))
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          placeholder="Ej: Revestimiento, Primera capa…"
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
          style={{
            flex: 1, border: '1px solid #e5e7eb', borderRadius: 6,
            padding: '6px 10px', fontSize: 12, outline: 'none'
          }}
        />
        <button type="button" className="calidad-btn-secondary" onClick={agregar} disabled={!texto.trim()}>
          + Agregar
        </button>
        <button type="button" className="calidad-btn-secondary" onClick={onQuitarGrupo}>
          Quitar partida
        </button>
      </div>
    </div>
  );
}

export default function ConfiguracionRegistros({ actividades, secciones, actividadesActuales, onGuardado, onCerrar }) {
  const [seleccion, setSeleccion] = useState(() => {
    const m = new Map();
    for (const item of actividadesActuales || []) {
      m.set(item.wbsCode, [...(item.protocolos || [])]);
    }
    return m;
  });
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const mapaSeccion = useMemo(() => {
    const m = {};
    for (const s of secciones) {
      if (s.wbsCode) m[s.wbsCode] = s;
    }
    return m;
  }, [secciones]);

  // Agrupa por sección, y dentro de cada sección agrupa por nombre — las
  // partidas con el mismo nombre se seleccionan/editan todas juntas.
  const grupos = useMemo(() => {
    const filtradas = busquedaTexto.trim()
      ? actividades.filter(a => a.nombre?.toLowerCase().includes(busquedaTexto.toLowerCase()))
      : actividades;

    const porSeccion = {};
    for (const act of filtradas) {
      if (!act.wbsCode) continue;
      const nivel1 = act.wbsCode.split('.')[0];
      if (!porSeccion[nivel1]) {
        const sec = mapaSeccion[nivel1];
        porSeccion[nivel1] = { wbs: nivel1, nombre: sec?.nombre || nivel1, orden: sec?.orden ?? 9999, porNombre: {} };
      }
      const nombre = act.nombre || '(sin nombre)';
      if (!porSeccion[nivel1].porNombre[nombre]) porSeccion[nivel1].porNombre[nombre] = [];
      porSeccion[nivel1].porNombre[nombre].push(act);
    }

    return Object.values(porSeccion)
      .sort((a, b) => a.orden - b.orden)
      .map(sec => ({
        ...sec,
        filas: Object.entries(sec.porNombre) // [[nombre, [act, ...]], ...]
      }));
  }, [actividades, mapaSeccion, busquedaTexto]);

  const protocolosDeGrupo = (acts) => {
    for (const a of acts) {
      const p = seleccion.get(a.wbsCode);
      if (p) return p;
    }
    return [];
  };

  const toggleFila = (acts) => {
    const todosSeleccionados = acts.every(a => seleccion.has(a.wbsCode));
    const protocolosCompartidos = protocolosDeGrupo(acts);
    setSeleccion(prev => {
      const next = new Map(prev);
      for (const a of acts) {
        todosSeleccionados ? next.delete(a.wbsCode) : next.set(a.wbsCode, [...protocolosCompartidos]);
      }
      return next;
    });
  };

  const setProtocolosGrupo = (acts, protocolos) => {
    setSeleccion(prev => {
      const next = new Map(prev);
      for (const a of acts) {
        next.set(a.wbsCode, protocolos);
      }
      return next;
    });
  };

  const handleGuardar = async () => {
    setError(null);
    const sinProtocolos = [...seleccion.entries()].filter(([, protocolos]) => protocolos.length === 0);
    if (sinProtocolos.length > 0) {
      const nombres = [...new Set(
        sinProtocolos.map(([wbsCode]) => actividades.find(a => a.wbsCode === wbsCode)?.nombre || wbsCode)
      )].join(', ');
      setError(`Falta agregar al menos un protocolo a: ${nombres}`);
      return;
    }
    setGuardando(true);
    try {
      const payload = [...seleccion.entries()].map(([wbsCode, protocolos]) => ({ wbsCode, protocolos }));
      const nuevas = await guardarConfigRegistroCalidad(payload);
      onGuardado(nuevas);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const totalPartidas = useMemo(
    () => grupos.reduce((sum, g) => sum + g.filas.length, 0),
    [grupos]
  );

  return (
    <div className="calidad-modal-overlay" onClick={onCerrar}>
      <div className="calidad-modal" onClick={e => e.stopPropagation()}>
        <div className="calidad-modal-header">
          <h3>Seleccionar partidas de Registros</h3>
          <p>
            Elige las partidas del programa a controlar y define, para cada una,
            uno o más protocolos (ej. partida "Tabique" → "Revestimiento", "Primera capa").
            Las que tienen el mismo nombre están agrupadas — un clic las selecciona todas.
            Cada partida seleccionada necesita al menos un protocolo.
          </p>
          <p style={{ marginTop: 8, color: '#6b7280', fontSize: 12 }}>
            Al guardar, se reparte automáticamente al azar qué cargo controla cada
            partida (80% Jefe de terreno, 60% Calidad, 20% Administrador de obra —
            una misma partida puede quedar con más de un cargo).
          </p>
        </div>

        <div style={{ padding: '0 24px 8px' }}>
          <input
            type="text"
            placeholder="Buscar partida…"
            value={busquedaTexto}
            onChange={e => setBusquedaTexto(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box',
              border: '1px solid #e5e7eb', borderRadius: 6,
              padding: '7px 12px', fontSize: 13, outline: 'none'
            }}
          />
        </div>

        <div className="calidad-modal-body">
          {grupos.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
              {busquedaTexto ? 'Sin resultados para esa búsqueda.' : 'No hay actividades disponibles en el programa activo.'}
            </p>
          ) : (
            grupos.map(grupo => {
              const todasActs = grupo.filas.flatMap(([, acts]) => acts);
              const todosGrupo = todasActs.every(a => seleccion.has(a.wbsCode));
              const algunoGrupo = todasActs.some(a => seleccion.has(a.wbsCode));
              const countSelGrupo = todasActs.filter(a => seleccion.has(a.wbsCode)).length;

              return (
                <div key={grupo.wbs} style={{ marginBottom: 12 }}>
                  {/* Encabezado de sección */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px', background: '#f3f4f6',
                    borderRadius: 6, marginBottom: 4,
                    border: '1px solid #e5e7eb'
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: '#374151', flex: 1 }}>
                      {grupo.nombre}
                    </span>
                    {countSelGrupo > 0 ? (
                      <span style={{
                        fontSize: 11, fontWeight: 600, color: '#15803d',
                        background: '#dcfce7', borderRadius: 10, padding: '1px 7px'
                      }}>
                        {countSelGrupo}/{todasActs.length}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>
                        {grupo.filas.length} tipo{grupo.filas.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Filas agrupadas por nombre */}
                  <div style={{ paddingLeft: 12 }}>
                    {grupo.filas.map(([nombre, acts]) => {
                      const todosFila = acts.every(a => seleccion.has(a.wbsCode));
                      const algunoFila = acts.some(a => seleccion.has(a.wbsCode));
                      const protocolos = protocolosDeGrupo(acts);

                      return (
                        <div key={nombre} style={{ marginBottom: 4 }}>
                          <div
                            className={`calidad-seccion-opcion ${todosFila ? 'seleccionada' : ''}`}
                            onClick={() => toggleFila(acts)}
                          >
                            <CheckboxIndeterminate
                              checked={todosFila}
                              indeterminate={algunoFila && !todosFila}
                              onChange={() => toggleFila(acts)}
                              onClick={e => e.stopPropagation()}
                            />
                            <span className="calidad-seccion-opcion-label">{nombre}</span>
                            {acts.length > 1 && (
                              <span style={{
                                fontSize: 11, fontWeight: 700, color: '#6b7280',
                                background: '#f3f4f6', borderRadius: 10,
                                padding: '1px 7px', marginLeft: 4, whiteSpace: 'nowrap'
                              }}>
                                ×{acts.length}
                              </span>
                            )}
                            {todosFila && (
                              <span style={{
                                fontSize: 11, fontWeight: 700,
                                color: protocolos.length > 0 ? '#15803d' : '#dc2626',
                                background: protocolos.length > 0 ? '#dcfce7' : '#fee2e2',
                                borderRadius: 10, padding: '1px 7px', marginLeft: 4, whiteSpace: 'nowrap'
                              }}>
                                {protocolos.length} protocolo{protocolos.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {todosFila && (
                            <PanelProtocolos
                              protocolos={protocolos}
                              onCambiar={(nuevos) => setProtocolosGrupo(acts, nuevos)}
                              onQuitarGrupo={() => toggleFila(acts)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
          {error && (
            <p style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{error}</p>
          )}
        </div>

        <div className="calidad-modal-footer">
          <span style={{ fontSize: 12, color: '#6b7280', marginRight: 'auto' }}>
            {seleccion.size} actividad{seleccion.size !== 1 ? 'es' : ''} seleccionada{seleccion.size !== 1 ? 's' : ''}
            {totalPartidas > 0 && (
              <span style={{ color: '#9ca3af' }}> ({totalPartidas} tipos únicos)</span>
            )}
          </span>
          <button className="calidad-btn-secondary" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </button>
          <button className="calidad-btn-primary" onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
